'use client';

import { HStack } from '@terra/ui-styled-system';
import { InformationCircleIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { IconButton } from '../../components/icon-button';
import { Tooltip } from '../../components/tooltip';
import { Field } from '../../components/field';
import type { MfaTotpSecretRevealProps } from './mfa.types';
import { SensitiveInput } from '../inputs/SensitiveInput';

export type MfaOtpTotpSecretFieldProps = {
  visible: boolean;
  storedTotpSecret: string;
  onTotpSecretChange?: (secret: string) => void;
  totpSecretReveal?: MfaTotpSecretRevealProps;
};
export function MfaOtpTotpSecretField({
  visible,
  storedTotpSecret,
  onTotpSecretChange,
  totpSecretReveal,
}: MfaOtpTotpSecretFieldProps) {
  const t = useTranslations('designSystem.mfa');

  if (!visible) {
    return null;
  }

  return (
    <Field.Root>
      <HStack alignItems="center" gap={2}>
        <Field.Label fontSize="sm" fontWeight="medium">
          {t('totpSecret')}
        </Field.Label>
        <Tooltip
          openDelay={50}
          positioning={{
            placement: 'top',
            strategy: 'fixed',
          }}
          title={t('totpSecretTooltip')}
          maxW="280px"
        >
          <IconButton
            variant="ghost"
            size="xs"
            aria-label="TOTP Secret Information"
          >
            <InformationCircleIcon size={14} />
          </IconButton>
        </Tooltip>
      </HStack>
      <Field.HelperText fontSize="0.8125rem" color="fg.muted">
        {t('totpSecretHelperText')}
      </Field.HelperText>
      {totpSecretReveal ? (
        <SensitiveInput
          value={storedTotpSecret}
          placeholder={t('totpSecretPlaceholder')}
          onChange={(value) => onTotpSecretChange?.(value)}
          fieldKey={totpSecretReveal.totpSecretFieldKey}
          allowViewingCredentials={totpSecretReveal.allowViewingCredentials}
          onCopy={totpSecretReveal.onCopy}
          copiedState={totpSecretReveal.copiedState}
          visibleState={totpSecretReveal.visibleState}
          onToggleVisibility={totpSecretReveal.onToggleVisibility}
          onCopySuccess={totpSecretReveal.onCopySuccess}
          type="password"
          isDefaultValue={totpSecretReveal.isDefaultSecretValue}
        />
      ) : (
        <Field.Input
          placeholder={t('totpSecretPlaceholder')}
          value={storedTotpSecret}
          onChange={(event) => onTotpSecretChange?.(event.target.value)}
        />
      )}
    </Field.Root>
  );
}
